import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowRight, FaHome, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getApiUrl } from '../config/api';
import { formatPrice } from '../utils/currencyPreference';
import './OffersDealsPage.css';

const defaultOffers = [
  { id: 'offer-1', badge: 'Member Deal', title: 'Luxury Ocean View Suite', details: 'Early check-in • 15% off', pricePerNight: 300, image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&q=80' },
  { id: 'offer-2', badge: 'Member Deal', title: 'Serenity Riverside Resort', details: 'Late check-out • 20% off', pricePerNight: 220, image: 'https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=1200&q=80' },
];

const sortOffersNewestFirst = (offers = []) => {
  const toTs = (offer) => {
    const raw =
      offer?.createdAt ||
      offer?.updatedAt ||
      offer?.discount?.createdAt ||
      offer?.discount?.updatedAt ||
      offer?.property?.createdAt ||
      offer?.property?.updatedAt;
    const ts = raw ? new Date(raw).getTime() : 0;
    return Number.isFinite(ts) ? ts : 0;
  };

  return [...offers].sort((a, b) => toTs(b) - toTs(a));
};

const ITEMS_PER_PAGE = 4;

const OffersDealsPage = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const response = await fetch(getApiUrl('/api/properties/offers-with-discounts'));
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          setOffers(defaultOffers);
        } else {
          const result = await response.json();
          if (result.success && result.offers && result.offers.length > 0) {
            setOffers(sortOffersNewestFirst(result.offers));
          } else {
            setOffers(defaultOffers);
          }
        }
      } catch (error) {
        console.error('Error fetching offers:', error);
        setOffers(defaultOffers);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, []);

  const totalPages = Math.ceil(offers.length / ITEMS_PER_PAGE);
  const paginatedOffers = offers.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const handleViewProperty = (item) => {
    if (item.propertyId) {
      navigate(`/property/${item.propertyId}`);
    }
  };

  return (
    <div className="offers-deals-page">
      <Navbar />
      <div className="offers-deals-container">
        <header className="offers-deals-header">
          <button type="button" className="btn-back" onClick={() => navigate(-1)} aria-label="Go back">
            ← Back
          </button>
          <h1 className="offers-deals-title">Offers & Deals</h1>
          <p className="offers-deals-subtitle">Special perks and discounts on stays</p>
          <button type="button" className="btn-home" onClick={() => navigate('/')}>
            <FaHome /> Home
          </button>
        </header>

        {loading ? (
          <div className="offers-deals-loading">
            <div className="offers-deals-spinner" />
            <p>Loading offers...</p>
          </div>
        ) : offers.length === 0 ? (
          <div className="offers-deals-empty">
            <p>No offers available at the moment. Check back soon!</p>
            <button type="button" className="btn-primary" onClick={() => navigate('/')}>
              <FaHome /> Back to Home
            </button>
          </div>
        ) : (
          <>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPage}
                className="offers-deals-grid"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                {paginatedOffers.map((item, index) => (
                  <motion.article
                    key={item.id || index}
                    className="offer-card"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    whileHover={{ y: -4 }}
                  >
                    <div className="offer-bg" style={{ backgroundImage: `url(${item.image})` }} />
                    <div className="offer-overlay" />
                    <div className="offer-content">
                      <h3 className="offer-title">{item.title}</h3>
                      <p className="offer-details">{item.details || ''}</p>
                      <div className="offer-footer">
                        <div className="offer-pricing">
                          {item.originalPrice && item.originalPrice !== item.pricePerNight ? (
                            <>
                              <span className="original-price">{formatPrice(item.originalPrice)}</span>
                              <span className="offer-price">
                                {formatPrice(item.pricePerNight)} <span>/ night</span>
                              </span>
                            </>
                          ) : (
                            <span className="offer-price">
                              {formatPrice(item.pricePerNight)} <span>/ night</span>
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn-view-offer"
                          onClick={() => handleViewProperty(item)}
                        >
                          View <FaArrowRight />
                        </button>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </motion.div>
            </AnimatePresence>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="offers-deals-pagination">
                <button
                  type="button"
                  className="offers-deals-page-btn offers-deals-page-btn-prev"
                  onClick={handlePrevious}
                  disabled={currentPage === 0}
                >
                  <FaChevronLeft /> Previous
                </button>
                <span className="offers-deals-page-info">
                  {currentPage + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  className="offers-deals-page-btn offers-deals-page-btn-next"
                  onClick={handleNext}
                  disabled={currentPage === totalPages - 1}
                >
                  Next <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default OffersDealsPage;
